<template>
  <v-container grid-list-lg fluid>
    <v-toolbar dense flat color="grey lighten-3">
      <v-toolbar-title>New Department</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn color="primary" dark @click="submit()">Save</v-btn>
      <v-btn color="primary" text @click="close()">Cancel</v-btn>
    </v-toolbar>
    <!-- <ModelTitle title="New Department" @close="close()" /> -->

    <v-card-text>
      <v-row>
        <v-col cols="12" sm="12">
          <v-text-field outlined :hide-details="nameErrors.length === 0" dense :error-messages="nameErrors"
            v-model="Class.name" label="Name"></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6" offset-md="6">
          <v-text-field outlined dense hide-details @focus="$event.target.select()" class="right-input"
            v-model="Class.defaultFee" prefix="$" label="Default monthly fee"></v-text-field>
        </v-col>
        <v-col cols="12">
          <DepartmentBillingPlan v-model="Class.metadata.departmentBilling" />
        </v-col>
        <v-col cols="12">
          <SpotHoldBillingSettings v-model="Class.metadata.spotHoldBilling" />
        </v-col>
        <v-col cols="12">
          <v-card outlined>
            <v-card-text class="d-flex flex-wrap align-center justify-space-between">
              <div class="pr-4">
                <div class="text-subtitle-1 font-weight-bold">Classroom assignment</div>
                <div class="caption grey--text">Turn this off for virtual or third-party departments that do not place students into Taleem classrooms.</div>
              </div>
              <v-switch v-model="Class.metadata.requiresClassroomAssignment" inset hide-details label="Required" class="mt-0" />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card outlined class="sibling-tier-card">
            <v-card-text>
              <div class="sibling-tier-heading">
                <div>
                  <div class="text-subtitle-1 font-weight-bold">Sibling fee tiers</div>
                  <div class="caption grey--text">Apply lower monthly fees when the same parent enrolls multiple active students in this department.</div>
                </div>
                <v-switch v-model="Class.metadata.siblingFeeDiscountEnabled" inset hide-details label="Enabled" class="mt-0" />
              </div>
              <v-expand-transition>
                <div v-if="Class.metadata.siblingFeeDiscountEnabled" class="sibling-tier-fields">
                  <div class="sibling-tier-base"><span>First student</span><strong>{{ Class.defaultFee | currency }}</strong></div>
                  <v-row v-for="(amount, index) in Class.metadata.siblingFeeTiers" :key="`sibling-tier-${index}`" align="center">
                    <v-col cols="10" sm="6"><v-text-field v-model.number="Class.metadata.siblingFeeTiers[index]" type="number" min="0" step="0.01" prefix="$" outlined dense hide-details :label="`${ordinal(index + 2)} student fee`" /></v-col>
                    <v-col cols="2"><v-btn icon color="error" :aria-label="`Remove ${ordinal(index + 2)} student tier`" @click="removeSiblingFeeTier(index)"><v-icon>mdi-delete-outline</v-icon></v-btn></v-col>
                  </v-row>
                  <v-btn color="primary" outlined small @click="addSiblingFeeTier"><v-icon left>mdi-plus</v-icon>Add next student tier</v-btn>
                  <div class="sibling-tier-note">The final amount continues for every additional sibling.</div>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12"><DepartmentSchedule v-model="Class.metadata" /></v-col>
        <v-col cols="12">
          <v-card outlined class="annual-charge-card">
            <v-card-text>
              <div class="annual-charge-heading">
                <div><div class="text-subtitle-1 font-weight-bold">Annual student charge</div><div class="caption grey--text">Automatically create one open parent invoice each year with one line per active student.</div></div>
                <v-switch v-model="Class.metadata.annualCharge.enabled" inset hide-details label="Enabled" class="mt-0" />
              </div>
              <v-expand-transition>
                <div v-if="Class.metadata.annualCharge.enabled" class="annual-charge-fields">
                  <v-row>
                    <v-col cols="12" md="6"><v-text-field v-model="Class.metadata.annualCharge.name" outlined dense hide-details label="Invoice line name" placeholder="Yearly book fee" /></v-col>
                    <v-col cols="12" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.amount" type="number" min="0.01" step="0.01" prefix="$" outlined dense hide-details label="Amount per student" /></v-col>
                    <v-col cols="12" md="3"><v-text-field :value="annualInvoiceDatePreview" outlined dense hide-details readonly label="Expected invoice date" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-select v-model.number="Class.metadata.annualCharge.startMonth" :items="monthOptions" item-text="text" item-value="value" outlined dense hide-details label="Annual cycle starts — month" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-select v-model.number="Class.metadata.annualCharge.startDay" :items="dayOfMonthOptions" outlined dense hide-details label="Day" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.invoiceDelayDays" type="number" min="0" max="365" outlined dense hide-details label="Create invoice after (days)" /></v-col>
                    <v-col cols="12" sm="6" md="3"><v-text-field v-model.number="Class.metadata.annualCharge.renewalProtectionDays" type="number" min="0" max="180" outlined dense hide-details label="Skip late joins within (days)" /></v-col>
                  </v-row>
                  <v-alert text color="primary" icon="mdi-information-outline" class="mb-0">Late joiners receive the full amount until the renewal-protection window. Students joining inside that window wait for the next annual cycle.</v-alert>
                </div>
              </v-expand-transition>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="12">
          <div class="mb-1">
            <div>Contact Information</div>
            <div>This information will be sent to anyone who fills out the application via email.</div>
          </div>
          <quill-editor :content="Class.contactInformation" :options="editorOption" @change="onContactInformationChange($event)" />
        </v-col>
        <v-col cols="12" sm="12">
          <div class="mb-1">
            <div>Contact Information For Pending Balance Letter</div>
            <div>This information will be sent to anyone whose balance is pending for this department.</div>
          </div>
          <quill-editor :content="Class.letterContactInformation" :options="editorOption" @change="onLetterContactInformationChange($event)" />
        </v-col>
        <v-col cols="12" sm="12">
          <quill-editor :content="Class.description" :options="editorOption" @change="onEditorChange($event)" />
          <!-- <v-textarea outlined dense hide-details auto-grow v-model="Class.description"
            :label="$t('labels.description')"></v-textarea> -->
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Collect Fees in Advance"
            hide-details
            outlined
            dense
            v-model="Class.metadata.preCollectFee"
          ></v-checkbox>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Count In Subscription"
            hide-details
            outlined
            dense
            v-model="Class.countInSubscription"
          ></v-checkbox>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-checkbox
            label="Kids Class Program"
            hide-details
            outlined
            dense
            v-model="Class.isChild"
          ></v-checkbox>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- <v-divider></v-divider>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="secondary" text @click="close()">Close</v-btn>
      <v-btn color="primary" :loading="loading" dark @click="submit()">Save</v-btn>
    </v-card-actions> -->
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import classService from "../service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'

import { quillEditor } from 'vue-quill-editor'
import DepartmentBillingPlan from "./DepartmentBillingPlan.vue";
import DepartmentSchedule from "./DepartmentSchedule.vue";
import SpotHoldBillingSettings from "./SpotHoldBillingSettings.vue";

export default {
  name: "create-Class",
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
  },
  data() {
    return {
      loading: false,
      dialog: false,
      editorOption: {
        placeholder: 'Description',
      },
      dayOptions: [
        { text: "Sunday", value: 0 },
        { text: "Monday", value: 1 },
        { text: "Tuesday", value: 2 },
        { text: "Wednesday", value: 3 },
        { text: "Thursday", value: 4 },
        { text: "Friday", value: 5 },
        { text: "Saturday", value: 6 },
      ],
      monthOptions: [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
      ].map((text, index) => ({ text, value: index + 1 })),
      dayOfMonthOptions: Array.from({ length: 31 }, (value, index) => index + 1),
      Class: {
        name: null,
        defaultFee: null,
        note: null,
        metadata: {
          preCollectFee: false,
          requiresClassroomAssignment: true,
          openWeekdays: [1, 2, 3, 4, 5],
          attendanceSchedule: {
            enabled: false,
            startTime: "17:30",
            endTime: "19:30",
            reminderOffsetMinutes: 30,
            reminderDirection: "after",
            reminderReference: "end",
            timeZone: "America/New_York",
          },
          siblingFeeDiscountEnabled: false,
          siblingFeeTiers: [],
          departmentBilling: {
            enabled: false,
            months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          },
          spotHoldBilling: {
            pricingMode: "FIXED",
            amount: 25,
            percentage: 25,
          },
          annualCharge: {
            enabled: false,
            name: "",
            amount: 0,
            startMonth: 9,
            startDay: 1,
            invoiceDelayDays: 15,
            renewalProtectionDays: 45,
          }
        }
      },
    };
  },
  components: { quillEditor, DepartmentBillingPlan, DepartmentSchedule, SpotHoldBillingSettings },
  mixins: [validationMixin],
  validations: {
    Class: {
      name: {
        required,
      },
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    nameErrors() {
      const errors = [];
      if (!this.$v.Class.name.$dirty) return errors;
      if (!this.$v.Class.name.required)
        errors.push(this.$t("validations.fieldIsRequired"));

      return errors;
    },
    annualInvoiceDatePreview() {
      const rule = this.Class.metadata.annualCharge;
      const start = this.$moment.utc([2024, Number(rule.startMonth) - 1, 1]);
      start.date(Math.min(Number(rule.startDay), start.daysInMonth()));
      return start.add(Math.max(Number(rule.invoiceDelayDays || 0) - 1, 0), "days").format("MMMM D");
    },
  },
  methods: {
    ordinal(value) {
      const mod100 = value % 100;
      if (mod100 >= 11 && mod100 <= 13) return `${value}th`;
      return `${value}${{ 1: "st", 2: "nd", 3: "rd" }[value % 10] || "th"}`;
    },
    addSiblingFeeTier() {
      const tiers = this.Class.metadata.siblingFeeTiers;
      tiers.push(
        tiers.length ? tiers[tiers.length - 1] : Number(this.Class.defaultFee || 0)
      );
    },
    removeSiblingFeeTier(index) {
      this.Class.metadata.siblingFeeTiers.splice(index, 1);
    },
    onEditorChange(value) {
      this.Class.description = value.html
    },
    onContactInformationChange(value) {
      this.Class.contactInformation = value.html
    },
    onLetterContactInformationChange(value) {
      this.Class.letterContactInformation = value.html
    },
    close() {
      this.empty();
      this.$router.go(-1)
    },
    empty() {
      this.Class = {
        name: null,
        defaultFee: null,
        note: null,
        metadata: {
          preCollectFee: false,
          requiresClassroomAssignment: true,
          openWeekdays: [1, 2, 3, 4, 5],
          attendanceSchedule: {
            enabled: false,
            startTime: "17:30",
            endTime: "19:30",
            reminderOffsetMinutes: 30,
            reminderDirection: "after",
            reminderReference: "end",
            timeZone: "America/New_York",
          },
          siblingFeeDiscountEnabled: false,
          siblingFeeTiers: [],
          departmentBilling: {
            enabled: false,
            months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
          },
          spotHoldBilling: {
            pricingMode: "FIXED",
            amount: 25,
            percentage: 25,
          },
          annualCharge: {
            enabled: false,
            name: "",
            amount: 0,
            startMonth: 9,
            startDay: 1,
            invoiceDelayDays: 15,
            renewalProtectionDays: 45,
          }
        }
      };
      this.$v.$reset();
    },
    submit() {
      this.$v.$touch();
      const annual = this.Class.metadata.annualCharge;
      const annualInvalid = annual.enabled &&
        (!(annual.name || "").trim() || Number(annual.amount) <= 0 ||
          Number(annual.invoiceDelayDays) < 0 || Number(annual.renewalProtectionDays) < 0);
      const siblingInvalid = this.Class.metadata.siblingFeeDiscountEnabled &&
        (!this.Class.metadata.siblingFeeTiers.length ||
          this.Class.metadata.siblingFeeTiers.some((amount) =>
            amount === "" || amount === null || !Number.isFinite(Number(amount)) || Number(amount) < 0
          ));
      const billing = this.Class.metadata.departmentBilling;
      const billingInvalid = billing.enabled && !billing.months.length;
      const hold = this.Class.metadata.spotHoldBilling;
      const holdInvalid = hold.pricingMode === "PERCENT"
        ? !Number.isFinite(Number(hold.percentage)) || Number(hold.percentage) <= 0 || Number(hold.percentage) > 100
        : !Number.isFinite(Number(hold.amount)) || Number(hold.amount) <= 0;
      if (this.$v.$invalid || annualInvalid || siblingInvalid || billingInvalid || holdInvalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: annualInvalid
              ? "Complete the annual charge settings"
              : siblingInvalid
                ? "Add a valid sibling fee tier"
                : billingInvalid
                  ? "Complete the monthly billing plan"
                : holdInvalid
                  ? "Enter a valid spot-hold fee"
                : this.$t("toastMessage.text.invalidFields"),
          });
      } else {
        this.loading = true;
        return classService
          .create(this.Class)
          .then((result) => {
            if (result.status) {
              this.$swal
                .mixin({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                })
                .fire({
                  icon: "success",
                  title: "Class is created",
                });
              this.loading = false;
              this.$emit("submit");
              this.close();
            }
          })
          .catch((err) => {
            this.loading = false;
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "error",
                title: err.data.message,
              });
          });
      }
    },
  },
};
</script>

<style scoped>
.annual-charge-card.v-card--outlined { border-color: #dce8dc; }
.sibling-tier-card.v-card--outlined { border-color: #dce8dc; }
.sibling-tier-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.sibling-tier-fields { margin-top: 20px; padding-top: 20px; border-top: 1px solid #edf1ed; }
.sibling-tier-base { display: flex; max-width: 360px; align-items: center; justify-content: space-between; margin-bottom: 12px; padding: 12px 14px; border-radius: 8px; background: #f4f8f4; color: #465568; }
.sibling-tier-note { display: inline-block; margin-left: 12px; color: #7b8799; font-size: 12px; }
.sibling-tier-card >>> .v-input__slot { border-radius: 8px; }
.annual-charge-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; }
.annual-charge-fields { margin-top: 20px; padding-top: 20px; border-top: 1px solid #edf1ed; }
.annual-charge-card >>> .v-input__slot { border-radius: 8px; }
@media (max-width: 600px) {
  .annual-charge-heading, .sibling-tier-heading { align-items: flex-start; flex-direction: column; }
  .sibling-tier-note { display: block; margin: 10px 0 0; }
}
/* .ql-editor>.ql-blank {
  min-height: 300px;
} */
</style>
